import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { WrapperHttpService } from '../../../../../../services/wrapper-http-service';
import { getQueryStringFromObject } from '../../../../../../../common/utils';
import { API_URL } from '../../../../../../../services/config';
import { UliEndpoint } from '../../constants/uli-endpoint';
import {
  UliItemActionParams,
  UliItemOverviewModel,
  UliItemOverviewStatuses,
  UliItemUliInfo,
} from '../../models/uli-item-overview.model';
import { CarrierModel } from '../../../../../../../common/models/carrier.model';
import { UliItemOverviewTruckInfoUpdateModel } from '../../models/uli-truck-info-update.model';
import { UliItemTruck } from '../../models/uli-truck.model';
import { UliActivityType } from '../../../../../../../common/constants/uli';
import { UliItemOverviewMainInfoUpdateModel } from '../../models/uli-main-info-update.model';
import { RequestHeader } from '../../../../../../constants/headers';

@Injectable({
  providedIn: 'root',
})
export class UliItemOverviewService {
  constructor(private http: WrapperHttpService) {}

  getUliOverview({ uli, compartmentId, type }: UliItemActionParams, hubId: number): Observable<UliItemOverviewModel> {
    const url = `${API_URL}${UliEndpoint.V2}${UliEndpoint.Uli}/${uli}${UliEndpoint.Overview}?${getQueryStringFromObject(
      {
        compartmentId,
        type,
      },
    )}`;
    const headers = new HttpHeaders({ [RequestHeader.IntrackHubId]: hubId.toString() });
    return this.http.withLoading().get<UliItemOverviewModel>(url, { headers });
  }

  getUliStatuses(
    { uli, compartmentId, type }: UliItemActionParams,
    hubId: number,
  ): Observable<UliItemOverviewStatuses> {
    const url = `${API_URL}${UliEndpoint.Uli}/${uli}${UliEndpoint.Statuses}?${getQueryStringFromObject({
      compartmentId,
      hubId,
      type,
    })}`;
    const headers = new HttpHeaders({ [RequestHeader.IntrackHubId]: hubId.toString() });
    return this.http.withLoading().get<UliItemOverviewStatuses>(url, { headers });
  }

  updateUliOverview(
    { uli, compartmentId, type }: UliItemActionParams,
    uliItem: UliItemUliInfo,
  ): Observable<UliItemUliInfo> {
    const url = `${API_URL}${UliEndpoint.Uli}/${uli}${UliEndpoint.Update}?${getQueryStringFromObject({
      compartmentId,
      type,
    })}`;

    return this.http.withLoading().put<UliItemUliInfo>(url, uliItem);
  }

  updateTruckDetails(
    hubId: number,
    compartmentId: number,
    type: UliActivityType,
    uli: string,
    oldTruckId: number,
    toUpdate: UliItemOverviewTruckInfoUpdateModel,
  ): Observable<UliItemTruck> {
    const url = `${API_URL}${UliEndpoint.Uli}/${uli}${UliEndpoint.Truck}?${getQueryStringFromObject({
      hubId,
      compartmentId,
      type,
      uli,
      oldTruckId,
    })}`;
    const headers = new HttpHeaders({ [RequestHeader.IntrackHubId]: hubId.toString() });

    return this.http.withLoading().post<UliItemTruck>(url, toUpdate, { headers });
  }

  updateMainInfoDetails(
    compartmentId: number,
    toUpdate: UliItemOverviewMainInfoUpdateModel,
    type: UliActivityType,
    uliNumber: string,
  ): Observable<UliItemUliInfo> {
    const url = `${API_URL}${UliEndpoint.Uli}/${uliNumber}${UliEndpoint.Update}?compartmentId=${compartmentId}&type=${type}`;

    return this.http.withLoading().put<UliItemUliInfo>(url, toUpdate);
  }

  getCarriers(searchStr: string): Observable<CarrierModel[]> {
    return this.http
      .withLoading()
      .get<CarrierModel[]>(
        `${API_URL}${UliEndpoint.WebUli}${UliEndpoint.Carriers}?` +
          getQueryStringFromObject({ filter: searchStr, limit: 10 }),
      );
  }

  notifySap(body: UliItemActionParams): Observable<void> {
    return this.http.post<void>(`${API_URL}${UliEndpoint.Uli}${UliEndpoint.SapNotify}`, body);
  }

  updateParkingSpot(hubId: number, truckId: number, parkingSpaceId: string | null): Observable<void> {
    const url = `${API_URL}${UliEndpoint.Trucks}/${truckId}${UliEndpoint.ParkingSpot}?${getQueryStringFromObject({
      hubId,
    })}`;
    const body = { parkingSpaceId };
    return this.http.withLoading().put<void>(url, body);
  }

  deleteParkingSpot(hubId: number, truckId: number): Observable<void> {
    const url = `${API_URL}${UliEndpoint.Trucks}/${truckId}${UliEndpoint.ParkingSpot}?${getQueryStringFromObject({
      hubId,
    })}`;
    return this.http.withLoading().delete<void>(url);
  }
}
